import useEventListener from "@pathwright/ui/src/components/hooks/useEventListener"
import {
  getCohortSectionBaseUrl,
  getCohortSectionUrlValues,
  getLocalizedUrl,
  getRelativeUrl,
  isRelativeUrl,
  isSchoolUrl
} from "@pathwright/web/src/modules/utils/urls"
import { useState } from "react"

// Hook intended to intercept <a> tags clicked on the Path, though could
// be extended to work platform-wide if we wanted - simply depends on what
// component uses the hook.
// We attempt to replace any "wildcard" ids in the url (represented by "_")
// with the corresponding value in the current url. This is convenient for
// linking to steps in a path that may not be in the same cohort but share
// the same sync source id.
function useInterceptedLinks() {
  const [node, setNode] = useState()

  useEventListener(
    "click",
    function (e) {
      // Attempt to find the closest anchor tag (potentially the target).
      const anchorTag = e.target.closest("a")
      // Ensure anchar tag is contained in the node.
      if (anchorTag && node?.contains(anchorTag)) {
        const href = anchorTag.getAttribute("href")
        const isAppLink =
          anchorTag.classList.contains("Link") ||
          // Also consider Buttons as app links when found outside of ContentText (in blocks).
          // For instance, we don't want to ignore a click on the MeetingButton,
          // but we do want to ignore a Button class with an href property as those
          // are intended for external nav.
          ((anchorTag.classList.contains("Button") ||
            anchorTag.classList.contains("chakra-button")) &&
            !anchorTag.closest(".ContentText"))
        const isBlankTarget = anchorTag.getAttribute("target") === "_blank"
        const isDownload = anchorTag.getAttribute("download") !== null
        const isSchoolNav =
          !!href &&
          !href.startsWith("#") &&
          !href.startsWith("javascript") &&
          (isRelativeUrl(href) || isSchoolUrl(href)) &&
          !href.startsWith("mailto:")
        const isExternalNav =
          e.getModifierState("Meta") ||
          isDownload ||
          // We'll ignore the target attr if the href is relatve (i.e. /library/...) or is a school url.
          (isBlankTarget && !isSchoolNav)

        // Totally ignore internal app links (inferred by anchor tag with .Link class).
        if (href && !isAppLink) {
          let url = href
          const urlValues = getCohortSectionUrlValues()
          const hrefUrlValues = getCohortSectionUrlValues(href)
          // Check to see if we have a wild cohort placeholder.
          if (hrefUrlValues.cohortId === "_") {
            // Override the wild "_" cohort placeholder with the cohort ID in the current url.
            const hrefMatchStr = getCohortSectionBaseUrl(hrefUrlValues)
            const cohortSectionUrl = getCohortSectionBaseUrl({
              ...hrefUrlValues,
              cohortId: urlValues.cohortId
            })
            url = url.replace(hrefMatchStr, cohortSectionUrl)

            // Prevent the default navigation behavior.
            e.preventDefault()

            // Now, handle the navigation, either opening the url in a new tab or
            // navigating internally.
            if (isExternalNav) {
              window.open(getLocalizedUrl(url), "_blank").focus()
            } else {
              App.navigate(getRelativeUrl(url))
            }
          } else {
            // We can still optimize the navigation experience by navigating internally
            // when that is the user's intent.
            if (!isExternalNav && isSchoolNav) {
              // Prevent the default navigation behavior.
              e.preventDefault()
              App.navigate(getRelativeUrl(url))
            }
          }
        }
      }
    },
    [node]
  )

  return setNode
}

export default useInterceptedLinks
